<script>
import bem from '@/utils/bem';
import SubNavigation from '@/components/ux/subNavigation/SubNavigation.vue';
import companyTabMixin from '@/mixins/companyTabMixin';
import AddPageModal from '@/components/pageContent/AddPageModal.vue';
import ModuleSelectModal from '@/components/pageContent/ModuleSelectModal.vue';
import ModuleRenderer from '@/components/pageContent/modules/ModuleRenderer.vue';
import { mapActions, mapState } from 'vuex';
import { Modal } from 'ant-design-vue';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import HelpCenterIcon from '@/components/svg/HelpCenterIcon.vue';
import ForeverStudiosLogo from '@/components/svg/ForeverStudiosLogo.vue';
import DoubleArrowIcon from '@/components/svg/DoubleArrowIcon.vue';
import MenuDividerLine from '@/components/svg/MenuDividerLine.vue';
import {
  UpOutlined,
  DownOutlined,
  DeleteOutlined,
  HolderOutlined,
} from '@ant-design/icons-vue';
import draggable from 'vuedraggable';
import { MODULE_TYPES, MODULE_TYPE_LABELS } from '@/constants/pageContent';
import { getFullPageKey } from '@/utils/pageKeyUtils';
import { mapColorsToHexObject } from '@/utils/colorUtils';

const b = bem('page-content-page');

export default {
  components: {
    SubNavigation,
    AddPageModal,
    ModuleSelectModal,
    ModuleRenderer,
    PencilIcon,
    HelpCenterIcon,
    ForeverStudiosLogo,
    DoubleArrowIcon,
    MenuDividerLine,
    UpOutlined,
    DownOutlined,
    DeleteOutlined,
    HolderOutlined,
    draggable,
  },
  mixins: [companyTabMixin],
  data() {
    return {
      isPageModalVisible: false,
      editingPage: null,
      isModuleModalVisible: false,
      selectedModuleType: null,
      isPreview: false,
      isMobile: false,
      MODULE_TYPES,
      MODULE_TYPE_LABELS,
      // Draft state: holds the draft page object when editing a published page
      activeDraft: null,
      isDraftLoading: false,
    };
  },
  computed: {
    ...mapState({
      pages: (state) => state.companyPages.companyPages,
      isCompanyPagesLoaded: (state) => state.companyPages.isCompanyPagesLoaded,
      pageContent: (state) => state.pageContent.pageContent,
      selectedPageId: (state) => state.pageContent.selectedPageId,
      currentCompanyColors: (state) => state.companies.currentCompanyColors,
    }),
    selectedPage() {
      if (!this.selectedPageId || !this.pages?.length) return null;
      return this.pages.find((p) => p.id === this.selectedPageId) || null;
    },
    sortedPageModules() {
      const blocks = Array.isArray(this.pageContent)
        ? this.pageContent
        : (this.pageContent?.blocks || []);
      return blocks
        .map((block) => {
          let parsedContent = {};
          if (block.content) {
            try {
              parsedContent = typeof block.content === 'string'
                ? JSON.parse(block.content)
                : block.content;
            } catch {
              parsedContent = {};
            }
          }
          return {
            ...block,
            parsedContent,
            moduleType: parsedContent.moduleType || block.name,
            templateId: parsedContent.templateId,
          };
        })
        .filter((block) => block.moduleType || block.name)
        .sort((first, second) => (first.index || 0) - (second.index || 0));
    },
    // WHY: True when company has no pages yet — next created page will be forced as home
    isFirstPage() {
      return this.filteredPages.length === 0;
    },
    // WHY: True when editing in draft mode — changes go to draft content
    isEditingDraft() {
      return !!this.activeDraft;
    },
    filteredPages() {
      return (this.pages || []).filter((page) => !page.isDeleted);
    },
    isLoading() {
      return !this.isCompanyPagesLoaded;
    },
    fullPageKey() {
      // WHY: When a draft is active, use the draft's URL directly as pageKey
      // (it has __draft__ prefix which getFullPageKey can't parse).
      // All content CRUD uses this key to target draft content.
      if (this.activeDraft) {
        return this.activeDraft.url;
      }
      if (!this.selectedPage) return '';
      return getFullPageKey(this.selectedPage, this.currentCompany?.pageKey);
    },
    companyColors() {
      return mapColorsToHexObject(this.currentCompanyColors);
    },
    headerPages() {
      return (this.pages || [])
        .filter((page) => !page.isDeleted && page.showInNav)
        .slice(0, 5);
    },
    lastBlockHasBackground() {
      if (this.sortedPageModules.length === 0) return false;
      const lastModule = this.sortedPageModules[this.sortedPageModules.length - 1];
      const lastIndex = this.sortedPageModules.length - 1;
      const templateId = lastModule?.templateId || lastModule?.parsedContent?.templateId;

      if (templateId === 'product-select' || templateId === 'gallery-select') {
        const ids = templateId === 'product-select'
          ? (lastModule?.parsedContent?.collectionIds || [])
          : (lastModule?.parsedContent?.galleryIds || []);
        const itemsCount = ids.length || 1;
        const effectiveIndex = this.getEffectiveBlockIndex(lastIndex);
        const lastItemIndex = effectiveIndex + itemsCount - 1;
        return lastItemIndex % 2 === 1 && !!this.companyColors.color3;
      }

      const bg = this.getModuleBackground(lastModule, lastIndex);
      return bg !== 'transparent';
    },
    nextBlockIndex() {
      // Индекс для нового модуля = количество существующих модулей
      return this.sortedPageModules.length;
    },
  },
  watch: {
    currentCompany: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadPages();
        }
      },
      immediate: true,
    },
    pages: {
      handler(newPages) {
        if (this.selectedPageId && newPages?.length) {
          if (!newPages.some((p) => p.id === this.selectedPageId)) {
            this.$store.commit('pageContent/SET_SELECTED_PAGE_ID', null);
          }
        }
      },
      deep: true,
    },
  },
  beforeUnmount() {
    this.$store.commit('pageContent/SET_SELECTED_PAGE_ID', null);
    this.$store.commit('pageContent/CLEAR_PAGE_CONTENT');
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getCompanyPagesAction: 'companyPages/getCompanyPages',
      createCompanyPageAction: 'companyPages/createCompanyPage',
      updateCompanyPageAction: 'companyPages/updateCompanyPage',
      deleteCompanyPageAction: 'companyPages/deleteCompanyPage',
      getDraftAction: 'companyPages/getDraft',
      createDraftAction: 'companyPages/createDraft',
      publishDraftAction: 'companyPages/publishDraft',
      discardDraftAction: 'companyPages/discardDraft',
      getPageContentAction: 'pageContent/getPageContent',
      createPageContentAction: 'pageContent/createPageContent',
      editPageContentAction: 'pageContent/editPageContent',
      deleteBlockAction: 'pageContent/deletePageContent',
      getCompanyColorsAction: 'companies/getCompanyColors',
    }),
    loadPages() {
      if (this.currentCompany?.id) {
        this.getCompanyPagesAction(this.currentCompany.id);
        this.getCompanyColorsAction(this.currentCompany.id);
      }
    },
    handleAddPage() {
      this.editingPage = null;
      this.isPageModalVisible = true;
    },
    handleEditPage(page) {
      this.editingPage = page;
      this.isPageModalVisible = true;
    },
    handleClosePageModal() {
      this.isPageModalVisible = false;
      this.editingPage = null;
    },
    async handleSavePage(pageData) {
      const companyId = this.currentCompany.id;
      let success;

      if (pageData.id) {
        success = await this.updateCompanyPageAction({
          id: pageData.id,
          companyId,
          pageData,
        });
      } else {
        success = await this.createCompanyPageAction({
          companyId,
          pageData,
        });
      }

      if (success) {
        this.isPageModalVisible = false;
        this.editingPage = null;
      }
    },
    async handleDeletePage(page) {
      const fullKey = getFullPageKey(page, this.currentCompany?.pageKey);

      let hasModules = false;
      try {
        const response = await this.$store.dispatch('pageContent/checkPageContent', fullKey);
        hasModules = Array.isArray(response) && response.length > 0;
      } catch {
        hasModules = false;
      }

      if (hasModules) {
        Modal.warning({
          title: 'Cannot Delete Page',
          content: `Please delete all modules from "${page.name}" before deleting the page.`,
          okText: 'OK',
          width: 800,
          centered: true,
          class: 'custom-modal',
        });
        return;
      }

      Modal.confirm({
        title: 'Delete Page',
        content: `Are you sure you want to delete "${page.name}"?`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        width: 800,
        centered: true,
        class: 'custom-modal',
        onOk: () => this.deleteCompanyPageAction({
          id: page.id,
          companyId: this.currentCompany.id,
        }),
      });
    },
    openModuleModal(moduleType) {
      this.selectedModuleType = moduleType;
      this.isModuleModalVisible = true;
    },
    closeModuleModal() {
      this.isModuleModalVisible = false;
      this.selectedModuleType = null;
    },
    handleModuleSelect({
      templateId, type, templateName, blockType, content,
    }) {
      if (this.selectedPage?.id && this.fullPageKey) {
        const formattedName = templateName
          ? templateName.toLowerCase().replace(/\s+/g, '_')
          : type;

        const moduleContent = content
          ? {
            ...JSON.parse(content),
            templateId,
            moduleType: type,
            blockType: blockType || null,
          }
          : {
            templateId,
            moduleType: type,
            blockType: blockType || null,
          };

        const maxIndex = this.sortedPageModules.reduce(
          (max, m) => Math.max(max, m.index || 0),
          -1,
        );

        this.createPageContentAction({
          page: this.fullPageKey,
          params: {
            name: formattedName,
            pageKey: this.fullPageKey,
            index: maxIndex + 1,
            content: JSON.stringify(moduleContent),
          },
        });
      }
      this.closeModuleModal();
    },
    async handleSelectPage(page) {
      this.activeDraft = null;
      this.$store.commit('pageContent/SET_SELECTED_PAGE_ID', page?.id || null);
      if (page?.id) {
        // WHY: Check if a draft exists for this page — if so, load draft content instead.
        // This lets the admin continue editing where they left off.
        const draft = await this.getDraftAction(page.id);
        if (draft) {
          this.activeDraft = draft;
          // WHY: Use draft.url directly — getFullPageKey can't parse __draft__ prefix
          this.getPageContentAction(draft.url);
        } else {
          const fullKey = getFullPageKey(page, this.currentCompany?.pageKey);
          this.getPageContentAction(fullKey);
        }
      }
    },
    handleDeleteModule(module) {
      Modal.confirm({
        title: 'Delete Module',
        content: `Are you sure you want to delete this ${MODULE_TYPE_LABELS[module.moduleType] || 'module'}?`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        width: 800,
        centered: true,
        class: 'custom-modal',
        onOk: () => this.deleteBlockAction({ id: module.id, page: this.fullPageKey }),
      });
    },
    async handleMoveModule(moduleId, direction) {
      if (this.selectedPage?.id) {
        const modules = [...this.sortedPageModules];
        const idx = modules.findIndex((m) => m.id === moduleId);
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;

        if (newIdx >= 0 && newIdx < modules.length) {
          const curr = modules[idx];
          const target = modules[newIdx];
          const currContent = curr.parsedContent || {};
          const targetContent = target.parsedContent || {};
          const currNewBg = (newIdx % 2 === 1 && this.companyColors.color3)
            ? this.companyColors.color3
            : 'transparent';
          const targetNewBg = (idx % 2 === 1 && this.companyColors.color3)
            ? this.companyColors.color3
            : 'transparent';
          const updatedCurrContent = JSON.stringify({
            ...currContent,
            backgroundColor: currNewBg,
          });
          const updatedTargetContent = JSON.stringify({
            ...targetContent,
            backgroundColor: targetNewBg,
          });

          await Promise.all([
            this.editPageContentAction({
              id: curr.id,
              page: this.fullPageKey,
              params: { index: newIdx, content: updatedCurrContent },
            }),
            this.editPageContentAction({
              id: target.id,
              page: this.fullPageKey,
              params: { index: idx, content: updatedTargetContent },
            }),
          ]);
        }
      }
    },
    getEffectiveBlockIndex(moduleIndex) {
      let effectiveIndex = 0;
      for (let i = 0; i < moduleIndex; i += 1) {
        const mod = this.sortedPageModules[i];
        const tplId = mod?.templateId || mod?.parsedContent?.templateId;
        if (tplId === 'product-select') {
          const ids = mod?.parsedContent?.collectionIds || [];
          effectiveIndex += ids.length || 1;
        } else if (tplId === 'gallery-select') {
          const ids = mod?.parsedContent?.galleryIds || [];
          effectiveIndex += ids.length || 1;
        } else {
          effectiveIndex += 1;
        }
      }
      return effectiveIndex;
    },
    getModuleBackground(module, index) {
      const templateId = module?.templateId || module?.parsedContent?.templateId;
      if (templateId === 'product-select' || templateId === 'gallery-select') {
        return 'transparent';
      }
      const effectiveIndex = this.getEffectiveBlockIndex(index);
      if (effectiveIndex % 2 === 1 && this.companyColors.color3) {
        return this.companyColors.color3;
      }
      return 'transparent';
    },
    getModuleLabel(type) {
      return MODULE_TYPE_LABELS[type] || 'Module';
    },
    handleModuleUpdate(updatedModule) {
      if (this.selectedPage?.isPublished && !this.isEditingDraft) return;
      this.editPageContentAction({
        id: updatedModule.id,
        page: this.fullPageKey,
        params: { content: updatedModule.content },
      });
    },
    handleSelectionUpdate(module, data) {
      // WHY: Don't update content when page is published and not in draft mode.
      // Some child components emit selection-change on mount which would
      // trigger unwanted PUT requests on read-only published pages.
      if (this.selectedPage?.isPublished && !this.isEditingDraft) return;

      if (data.content) {
        const currentContent = module.parsedContent || {};
        const newParsedContent = data.parsedContent || JSON.parse(data.content);

        // Check if galleries or collections data has changed (for renamed items)
        const galleriesChanged = JSON.stringify(currentContent.galleries)
          !== JSON.stringify(newParsedContent.galleries);
        const collectionsChanged = JSON.stringify(currentContent.collections)
          !== JSON.stringify(newParsedContent.collections);

        if (currentContent.startWithColor !== newParsedContent.startWithColor
            || currentContent.backgroundColor !== newParsedContent.backgroundColor
            || galleriesChanged
            || collectionsChanged) {
          const updatedContent = {
            ...currentContent,
            ...newParsedContent,
            templateId: module.templateId || currentContent.templateId,
            moduleType: module.moduleType || currentContent.moduleType,
          };

          this.editPageContentAction({
            id: module.id,
            page: this.fullPageKey,
            params: { content: JSON.stringify(updatedContent) },
          });
        }
      }
    },
    // ── Draft actions ──

    async handleStartEditing() {
      if (!this.selectedPage) return;
      this.isDraftLoading = true;
      const draft = await this.createDraftAction({
        pageId: this.selectedPage.id,
        companyId: this.currentCompany.id,
      });
      if (draft) {
        this.activeDraft = draft;
        // WHY: Use draft.url directly — getFullPageKey can't parse __draft__ prefix.
        // This ensures content is loaded from the draft's pageKey, not the original.
        this.$store.commit('pageContent/CLEAR_PAGE_CONTENT');
        await this.getPageContentAction(draft.url);
      }
      this.isDraftLoading = false;
    },

    async handlePublishDraft() {
      if (!this.activeDraft) return;
      this.isDraftLoading = true;
      const published = await this.publishDraftAction({
        draftId: this.activeDraft.id,
        companyId: this.currentCompany.id,
      });
      this.activeDraft = null;
      this.isDraftLoading = false;
      if (published && this.selectedPage) {
        this.$store.commit('pageContent/CLEAR_PAGE_CONTENT');
        const fullKey = getFullPageKey(
          this.selectedPage,
          this.currentCompany?.pageKey,
        );
        await this.getPageContentAction(fullKey);
      }
    },

    async handleDiscardDraft() {
      if (!this.activeDraft) return;
      Modal.confirm({
        title: 'Discard Draft',
        content: 'Are you sure you want to discard all draft changes? This cannot be undone.',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Cancel',
        width: 800,
        centered: true,
        class: 'custom-modal',
        onOk: async () => {
          this.isDraftLoading = true;
          await this.discardDraftAction({
            draftId: this.activeDraft.id,
            companyId: this.currentCompany.id,
          });
          this.activeDraft = null;
          this.isDraftLoading = false;
          if (this.selectedPage) {
            // WHY: Clear cached content first, then reload from published page
            // to ensure we don't show stale draft content.
            this.$store.commit('pageContent/CLEAR_PAGE_CONTENT');
            const fullKey = getFullPageKey(
              this.selectedPage,
              this.currentCompany?.pageKey,
            );
            await this.getPageContentAction(fullKey);
          }
        },
      });
    },

    async handleTogglePublish() {
      if (!this.selectedPage) return;
      const newPublishState = !this.selectedPage.isPublished;
      await this.updateCompanyPageAction({
        id: this.selectedPage.id,
        companyId: this.currentCompany.id,
        pageData: {
          ...this.selectedPage,
          isPublished: newPublishState,
          publishedAt: newPublishState ? new Date().toISOString() : null,
        },
      });
    },
    handlePreviewPage() {
      if (this.isPreview) {
        this.isMobile = false;
      }
      this.isPreview = !this.isPreview;
    },
    async handlePageDragEnd() {
      const companyId = this.currentCompany?.id;
      if (!companyId) return;

      const updates = this.filteredPages.map((page, index) => this.updateCompanyPageAction({
        id: page.id,
        companyId,
        pageData: {
          ...page,
          footerOrder: index,
        },
      }));

      await Promise.all(updates);
    },
  },
};
</script>

<template>
  <div :class="b()">
    <a-spin :spinning="!isCurrentCompanyLoaded || isLoading">
      <SubNavigation :company-id="companyId" active-tab="page-content" />

      <div :class="b('content')">
        <div :class="b('left-sidebar')">
          <div :class="b('sidebar-title')">Pages:</div>
          <draggable
            v-if="filteredPages && filteredPages.length > 0"
            :list="filteredPages"
            :class="b('pages-list')"
            item-key="id"
            handle=".page-content-page__drag-handle"
            :ghost-class="b('drag-placeholder')"
            :animation="200"
            :sort="true"
            @end="handlePageDragEnd"
          >
            <template #item="{ element: page }">
              <div
                :class="b('page-item')"
                tabindex="0"
                @click="handleSelectPage(page)"
                @keydown.enter="handleSelectPage(page)"
              >
                <div :class="b('drag-handle')">
                  <HolderOutlined />
                </div>
                <span :class="b('page-name', { selected: selectedPage?.id === page.id })">
                  {{ page.name }}
                </span>
                <div :class="b('page-actions')">
                  <button
                    :class="b('icon-btn')"
                    title="Edit page"
                    @click.stop="handleEditPage(page)"
                  >
                    <PencilIcon />
                  </button>
                  <!-- WHY: Home page cannot be deleted — it's the company root -->
                  <button
                    v-if="!page.isHomePage"
                    :class="b('icon-btn', { delete: true })"
                    @click.stop="handleDeletePage(page)"
                  >
                    <div :class="b('icon-btn-delete')" />
                  </button>
                </div>
              </div>
            </template>
          </draggable>
          <button :class="b('add-page-btn')" @click="handleAddPage">
            + Add Page
          </button>
        </div>
        <div v-if="selectedPage && isPreview" :class="b('toggle-row')">
          <span :class="b('toggle-label')">Desktop</span>
          <button
            type="button"
            role="switch"
            :aria-checked="isMobile"
            :class="b('toggle', { active: isMobile })"
            @click="isMobile = !isMobile"
          >
            <span :class="b('toggle-ball', { active: isMobile })" />
          </button>
          <span :class="b('toggle-label')">Mobile</span>
        </div>
      </div>
      <div :class="b('center-content')">
        <h2 v-if="!selectedPage" :class="b('center-title')">
          Select a page to begin crafting
        </h2>
        <h2 v-else :class="b('center-title')">
          {{ `Selected page > ${selectedPage.name}` }}
        </h2>
        <!-- Draft banner -->
        <div v-if="isEditingDraft && !isPreview" :class="b('draft-banner')">
          You are editing a draft. Changes won't be visible until published.
        </div>

        <div v-if="selectedPage" :class="b('page-actions-row', { preview: isPreview })">
          <!-- Publish/Unpublish: only when not in draft mode and not previewing -->
          <button
            v-if="!isPreview && !isEditingDraft"
            :class="b('publish-btn')"
            @click="handleTogglePublish"
          >
            {{ selectedPage.isPublished ? 'UNPUBLISH' : 'PUBLISH' }}
          </button>

          <!-- Edit button: creates a draft for published pages that don't have one -->
          <button
            v-if="!isPreview && !isEditingDraft && selectedPage.isPublished"
            :class="b('edit-draft-btn')"
            :disabled="isDraftLoading"
            @click="handleStartEditing"
          >
            {{ isDraftLoading ? 'CREATING DRAFT...' : 'EDIT' }}
          </button>

          <!-- Draft actions: Publish Draft / Discard Draft -->
          <button
            v-if="!isPreview && isEditingDraft"
            :class="b('publish-draft-btn')"
            :disabled="isDraftLoading"
            @click="handlePublishDraft"
          >
            {{ isDraftLoading ? 'PUBLISHING...' : 'PUBLISH CHANGES' }}
          </button>
          <button
            v-if="!isPreview && isEditingDraft"
            :class="b('discard-draft-btn')"
            :disabled="isDraftLoading"
            @click="handleDiscardDraft"
          >
            DISCARD DRAFT
          </button>

          <button :class="b('preview-link')" @click="handlePreviewPage">
            {{isPreview ? 'BACK TO EDITS' : 'PREVIEW'}}
          </button>
        </div>
        <div
          v-if="isPreview"
          :class="b('preview-navigation', { mobile: isMobile })"
          :style="{ backgroundColor: companyColors.color1 || '#242424' }"
        >
          <div :class="b('preview-nav-container', { mobile: isMobile })">
            <div v-if="isMobile" :class="b('burger-lines-wrapper')">
              <div :class="b('burger-line')" />
              <div :class="b('burger-line')" />
              <div :class="b('burger-line')" />
            </div>
            <div :class="b('preview-nav-logo', { mobile: isMobile })">
              <img
                v-if="currentCompany?.logoImageUri"
                :src="currentCompany.logoImageUri"
                :alt="currentCompany?.displayName"
              />
            </div>
            <div :class="b('preview-nav-links')" v-if="!isMobile">
                <span
                  v-for="page in headerPages"
                  :key="page.id"
                  :class="b('preview-nav-link')"
                >
                  {{ page.name }}
                </span>
            </div>
            <div :class="b('preview-nav-help')" v-if="!isMobile">
              <HelpCenterIcon :fill-color="companyColors.color1" stroke-color="#fff" />
              <span :class="b('preview-nav-help-text')">Help Center</span>
            </div>
          </div>
        </div>

        <div
          v-if="sortedPageModules.length > 0"
          :class="b('blocks-list', { preview: isPreview, 'preview-mobile': isPreview && isMobile })"
        >
          <div
            v-for="(module, index) in sortedPageModules"
            :key="module.id"
            :class="b('block-row')"
            :style="isPreview ? {
              width: '100%',
              backgroundColor: getModuleBackground(module, index),
            } : {}"
          >
            <div
              :class="b('block-content')"
              :style="isPreview
                ? { width: '100%', display: 'flex', justifyContent: 'center' }
                : {}"
            >
              <ModuleRenderer
                :module-data="module"
                :is-edit-mode="!isPreview
                  && (!selectedPage.isPublished || isEditingDraft)"
                :is-mobile="isMobile"
                :block-index="getEffectiveBlockIndex(index)"
                :company-colors="companyColors"
                :company-id="companyId"
                @update="handleModuleUpdate"
                @selection-change="(data) => handleSelectionUpdate(module, data)"
              />
            </div>

            <div
              v-if="!isPreview && (!selectedPage.isPublished || isEditingDraft)"
              :class="b('block-controls')"
            >
              <button
                v-if="index > 0"
                :class="b('block-control-btn')"
                title="Move up"
                @click="handleMoveModule(module.id, 'up')"
              >
                <UpOutlined />
              </button>
              <button
                :class="b('block-control-btn', { delete: true })"
                title="Delete module"
                @click="handleDeleteModule(module)"
              >
                <DeleteOutlined />
              </button>
              <button
                v-if="index < sortedPageModules.length - 1"
                :class="b('block-control-btn')"
                title="Move down"
                @click="handleMoveModule(module.id, 'down')"
              >
                <DownOutlined />
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="isPreview"
          :class="b('back-to-top', { mobile: isMobile, 'no-top-padding': !lastBlockHasBackground })"
        >
          BACK TO THE TOP
        </div>
        <div
          v-if="isPreview"
          :class="b('preview-footer', { mobile: isMobile })"
          :style="{ backgroundColor: companyColors.color1 || '#242424' }"
        >
          <div :class="b('preview-footer-container', { mobile: isMobile })">
            <div :class="b('preview-footer-left')">
              <div :class="b('preview-footer-logo')" v-if="!isMobile">
                <img
                  v-if="currentCompany?.logoImageUri"
                  :src="currentCompany.logoImageUri"
                  :alt="currentCompany?.displayName"
                />
              </div>
              <span :class="b('preview-footer-text', { mobile: isMobile })">
                Powered by
                <span :class="b('preview-footer-bold', { mobile: isMobile })">FS POD</span>
              </span>
              <span :class="b('preview-footer-divider', { mobile: isMobile })" v-if="!isMobile" />
              <span :class="b('preview-footer-text')" v-if="!isMobile">Learn More</span>
            </div>
            <div :class="b('preview-footer-right', { mobile: isMobile })">
              <div :class="b('preview-footer-fs-logo')" v-if="!isMobile">
                <ForeverStudiosLogo />
              </div>
              <span :class="b('preview-footer-text', { mobile: isMobile })">Forever Studios</span>
              <span :class="b('preview-footer-divider', { mobile: isMobile })" />
              <span :class="b('preview-footer-text', { mobile: isMobile })">Terms</span>
              <span :class="b('preview-footer-divider', { mobile: isMobile })" />
              <span :class="b('preview-footer-text', { mobile: isMobile })">Privacy Policy</span>
            </div>
          </div>
          <div :class="b('preview-footer-pages', { mobile: isMobile })">
              <span
                v-for="page in filteredPages"
                :key="page.id"
                :class="b('preview-footer-page')"
              >
                {{ page.name }}
              </span>
          </div>
        </div>
        <div
          v-if="isPreview && isMobile"
          :class="b('mobile-dropdown-menu')"
          :style="{ backgroundColor: companyColors.color1 || '#242424' }"
        >
          <div :class="b('mobile-menu-header')">
            <div :class="b('mobile-menu-close')">
              <span :class="b('mobile-menu-close-line')" />
              <span :class="b('mobile-menu-close-line')" />
            </div>
            <div :class="b('mobile-menu-logo')">
              <img
                v-if="currentCompany?.logoImageUri"
                :src="currentCompany.logoImageUri"
                :alt="currentCompany?.displayName"
              />
            </div>
          </div>
          <div :class="b('mobile-menu-items')">
            <template v-for="(page, index) in headerPages" :key="page.id">
              <div :class="b('mobile-menu-item')">
                <span :class="b('mobile-menu-item-name')">{{ page.name }}</span>
                <DoubleArrowIcon :class="b('mobile-menu-item-icon')" />
              </div>
              <MenuDividerLine v-if="index < headerPages.length - 1" />
            </template>
          </div>
        </div>

        <!-- Module editing only when unpublished or draft active -->
        <div
          v-if="selectedPage && !isPreview
            && (!selectedPage.isPublished || isEditingDraft)"
          :class="b('add-modules-section')"
        >
          <div :class="b('modules-separator')" />
          <h3 :class="b('add-modules-title')">
            Start arranging your modules by clicking the arrows on the right.
          </h3>
          <div :class="b('modules-container')">
            <div :class="b('modules-row')">
              <button
                :class="b('module-btn', { large: true })"
                @click="openModuleModal(MODULE_TYPES.INFO)"
              >
                <span :class="b('module-btn-icon')">+</span>
                <span>INFO MODULE</span>
              </button>
              <button
                :class="b('module-btn', { large: true })"
                @click="openModuleModal(MODULE_TYPES.PRODUCT)"
              >
                <span :class="b('module-btn-icon')">+</span>
                <span>PRODUCT MODULE</span>
              </button>
              <button
                :class="b('module-btn', { large: true })"
                @click="openModuleModal(MODULE_TYPES.GALLERY)"
              >
                <span :class="b('module-btn-icon')">+</span>
                <span>PHOTO GALLERY MODULE</span>
              </button>
            </div>
            <div :class="b('modules-row')">
              <button
                :class="b('module-btn', { wide: true })"
                @click="openModuleModal(MODULE_TYPES.CTA)"
              >
                + call to action module
              </button>
              <button
                :class="b('module-btn', { wide: true })"
                @click="openModuleModal(MODULE_TYPES.PRINT_PAL)"
              >
                + print pal
              </button>
            </div>
          </div>
        </div>
      </div>
    </a-spin>

    <AddPageModal
      :visible="isPageModalVisible"
      :page-key="currentCompany?.pageKey || ''"
      :editing-page="editingPage"
      :existing-pages="filteredPages"
      :is-first-page="isFirstPage"
      @close="handleClosePageModal"
      @save="handleSavePage"
    />

    <ModuleSelectModal
      :visible="isModuleModalVisible"
      :module-type="selectedModuleType"
      :company-id="companyId"
      :block-index="nextBlockIndex"
      :company-colors="companyColors"
      @close="closeModuleModal"
      @select="handleModuleSelect"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';
@import '@/styles/fonts';

.page-content-page {
  &__content {
    display: flex;
    justify-content: flex-start;
    padding: 32px 40px 0;
    background: #fff;
    border-top: 1px solid #e8e8e8;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(32) vw-full(40) 0;
    }
  }

  &__left-sidebar {
    flex-shrink: 0;
    padding-right: 24px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: start;
    gap: 12px;

    @include mediaMinMax(1025, $normal) {
      padding-right: vw-full(24);
      gap: vw-full(12);
    }
  }

  &__sidebar-title {
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #293241;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }
  }

  &__pages-list {
    display: flex;
    flex-direction: column;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }
  }

  &__page-item {
    display: flex;
    width: 214px;
    height: 40px;
    align-items: center;
    justify-content: flex-start;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #293241;
    border-radius: 12px;
    border: 1px solid #ddd;
    background: #DDD;
    cursor: pointer;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(214);
      height: vw-full(40);
      font-size: vw-full(16);
      border-radius: vw-full(12);
    }

    &.sortable-chosen {
      opacity: 1;
    }

    &.sortable-drag {
      opacity: 1;
    }
  }

  &__drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 40px;
    cursor: grab;
    color: #7f7f7f;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(24);
      height: vw-full(40);
    }

    &:active {
      cursor: grabbing;
    }

    &:hover {
      color: #293241;
    }
  }

  &__drag-placeholder {
    opacity: 0.5;
  }

  &__page-name {
    width: 124px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #293241;
    border-radius: 12px;
    border: 1px solid #7F7F7F;
    background: #FFF;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(124);
      height: vw-full(40);
      font-size: vw-full(14);
      border-radius: vw-full(12);
    }
  }

  &__page-actions {
    display: flex;
    gap: 6px;
    padding-left: 12px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(6);
      padding-left: vw-full(12);
    }
  }

  &__icon-btn {
    width: 16px;
    height: 16px;
    background: none;
    border: none;
    cursor: pointer;
    color: #293241;
    display: flex;
    align-items: center;
    justify-content: center;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(4) 0;
    }

    &:hover {
      color: #1890ff;
    }

    &_delete:hover {
      color: #ff4d4f;
    }
  }

  &__icon-btn-delete {
    width: 21px;
    height: 16px;
    position: relative;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(21);
      height: vw-full(16);
    }

    &:before,
    &:after {
      display: block;
      width: 18px;
      height: 1px;
      background: #7F7F7F;
      content: '';
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);

      @include mediaMinMax(1025, $normal) {
        width: vw-full(18);
        height: vw-full(1);
      }
    }

    &:before {
      transform: rotate(45deg);
    }

    &:after {
      transform: rotate(-45deg);
    }
  }

  &__add-page-btn {
    width: 148px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #7f7f7f;
    border-radius: 12px;
    border: 1px solid #ddd;
    background: #fff;
    cursor: pointer;
    text-align: left;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(148);
      height: vw-full(40);
      font-size: vw-full(16);
      border-radius: vw-full(12);
    }

    &:hover {
      border-color: #293241;
    }
  }

  &__toggle-row {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    align-self: flex-start;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
    }
  }

  &__toggle {
    width: 56px;
    height: 28px;
    border-radius: 14px;
    border: 2px solid #242424;
    background: #fff;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    padding: 0;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(56);
      height: vw-full(28);
      border-radius: vw-full(14);
      border-width: vw-full(2);
    }
  }

  &__toggle-ball {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #242424;
    transition: all 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      left: vw-full(2);
      width: vw-full(20);
      height: vw-full(20);
    }

    &_active {
      left: calc(100% - 22px);

      @include mediaMinMax(1025, $normal) {
        left: calc(100% - #{vw-full(22)});
      }
    }
  }

  &__toggle-label {
    color: var(--pro-dark-gray, #242424);
    text-align: center;
    font-family: Montserrat;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }
  }

  &__center-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  &__center-title {
    font-family: Montserrat, sans-serif;
    font-size: 28px;
    font-weight: 500;
    color: #293241;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
      letter-spacing: vw-full(0.5);
    }
  }

  &__content-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    margin-top: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      margin-top: vw-full(24);
    }
  }

  &__modules-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
    margin-top: 20px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(40);
      margin-top: vw-full(20);
    }
  }

  &__modules-row {
    display: flex;
    justify-content: center;
    gap: 40px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(40);
    }
  }

  &__module-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ddd;
    border-radius: 20px;
    background: #fff;
    cursor: pointer;
    color: #595959;
    text-align: center;
    font-family: Montserrat, sans-serif;
    font-size: 28px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
      border-radius: vw-full(20);
    }

    &:hover {
      color: #FFFFFF;
      background: #242424;
      transition: all 0.2s ease;
      box-shadow: 4px 4px 8px 0 rgba(36, 36, 36, 0.35);
    }

    &:hover > span {
      color: #FFFFFF;
    }

    &_large {
      width: 440px;
      height: 200px;
      flex-direction: column;
      gap: 5px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(440);
        height: vw-full(200);
        gap: vw-full(5);
      }
    }

    &_wide {
      width: 680px;
      height: 80px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(680);
        height: vw-full(80);
      }
    }
  }

  &__module-btn-icon {
    font-size: 56px;
    line-height: 1;
    font-weight: 200;
    color: #595959;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(56);
    }
  }

  &__right-sidebar {
    display: flex;
    flex-direction: row;
    gap: 20px;
    flex-shrink: 0;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(20);
    }
  }

  &__action-btn {
    width: 218px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #242424;
    border-radius: 12px;
    border: 1px solid #242424;
    background: #FFF;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(218);
      height: vw-full(48);
      font-size: vw-full(16);
      border-radius: vw-full(12);
      letter-spacing: vw-full(0.5);
    }

    &:hover {
      background: #f5f5f5;
    }

    &_outline {
      background: #fff;
    }
  }

  &__page-name_selected {
    color: #FFF;
    background: #242424;
  }

  &__modules-list {
    width: 100%;
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @include mediaMinMax(1025, $normal) {
      margin-top: vw-full(40);
      gap: vw-full(16);
    }
  }

  &__modules-list-title {
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #293241;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }
  }

  &__module-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 12px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(16) vw-full(20);
      border-radius: vw-full(12);
    }
  }

  &__module-info {
    display: flex;
    flex-direction: column;
    gap: 4px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(4);
    }
  }

  &__module-type {
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #293241;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__module-template {
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    color: #7f7f7f;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
    }
  }

  &__module-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }
  }

  &__module-control-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 6px;
    cursor: pointer;
    color: #595959;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(32);
      height: vw-full(32);
      border-radius: vw-full(6);
    }

    &:hover {
      background: #e8e8e8;
      border-color: #bbb;
    }

    &_delete {
      color: #ff4d4f;

      &:hover {
        background: #fff1f0;
        border-color: #ff4d4f;
      }
    }
  }

  &__view-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-top: 32px;
    padding: 16px 0;
    border-top: 1px solid #e8e8e8;

    @include mediaMinMax(1025, $normal) {
      margin-top: vw-full(32);
      padding: vw-full(16) 0;
    }
  }

  &__view-toggle {
    display: flex;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }
  }

  &__device-toggle {
    display: flex;
    gap: 4px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(4);
    }
  }

  &__module-wrapper {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 12px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      padding: vw-full(16);
      border-radius: vw-full(12);
    }
  }

  &__module-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;
    border-bottom: 1px solid #e8e8e8;

    @include mediaMinMax(1025, $normal) {
      padding-bottom: vw-full(12);
    }
  }

  &__page-actions-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 40px;
    margin: 20px 0 50px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(40);
      margin: vw-full(20) 0 vw-full(50);
    }
    &_preview {
      margin: 34px 0 54px;

      @include mediaMinMax(1025, $normal) {
        margin: vw-full(34) 0 vw-full(54);
      }
    }
  }

  &__view-toggle {
    display: flex;
    gap: 4px;
    border: 1px solid #7f7f7f;
    border-radius: 20px;
    padding: 3px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(4);
      border-radius: vw-full(20);
      padding: vw-full(3);
    }
  }

  &__view-toggle-btn {
    padding: 6px 20px;
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #7f7f7f;
    background: transparent;
    border: none;
    border-radius: 17px;
    cursor: pointer;
    transition: all 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(6) vw-full(20);
      font-size: vw-full(14);
      border-radius: vw-full(17);
    }

    &:hover {
      background: rgba(127, 127, 127, 0.1);
    }

    &_active {
      background: #7f7f7f;
      color: #fff;

      &:hover {
        background: #7f7f7f;
      }
    }
  }

  &__publish-btn {
    width: 400px;
    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    background: #fff;
    box-shadow: 4px 4px 8px 0 rgba(36, 36, 36, 0.35);
    color: #242424;
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 500;
    text-transform: uppercase;
    border: 1px solid #242424;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #242424;
      color: #fff;
    }

    @include mediaMinMax(1025, $normal) {
      width: vw-full(400);
      height: vw-full(52);
      font-size: vw-full(24);
      border-radius: vw-full(20);
    }
  }

  &__draft-banner {
    width: 100%;
    padding: 12px 24px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 8px;
    color: #856404;
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    margin-bottom: 16px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(12) vw-full(24);
      border-radius: vw-full(8);
      font-size: vw-full(14);
      margin-bottom: vw-full(16);
    }
  }

  &__edit-draft-btn {
    width: 200px;
    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    background: #242424;
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 500;
    text-transform: uppercase;
    border: 1px solid #242424;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #333;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    @include mediaMinMax(1025, $normal) {
      width: vw-full(200);
      height: vw-full(52);
      font-size: vw-full(20);
      border-radius: vw-full(20);
    }
  }

  &__publish-draft-btn {
    width: 300px;
    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    background: #28a745;
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 500;
    text-transform: uppercase;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #218838;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    @include mediaMinMax(1025, $normal) {
      width: vw-full(300);
      height: vw-full(52);
      font-size: vw-full(20);
      border-radius: vw-full(20);
    }
  }

  &__discard-draft-btn {
    width: 250px;
    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    background: #fff;
    color: #dc3545;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 500;
    text-transform: uppercase;
    border: 1px solid #dc3545;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #dc3545;
      color: #fff;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    @include mediaMinMax(1025, $normal) {
      width: vw-full(250);
      height: vw-full(52);
      font-size: vw-full(20);
      border-radius: vw-full(20);
    }
  }

  &__preview-link {
    background: none;
    border: none;
    color: #5e5d5d;
    text-align: center;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: normal;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #242424;
    }

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }
  }

  &__blocks-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 64px;
    @include mediaMinMax(1025, $normal) {
      gap: vw-full(64);
    }
    &_preview {
      gap: 0;
    }
    &_preview-mobile {
      width: 412px;
    }
  }

  &__block-row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 64px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(64);
    }
  }

  &__block-content {
    flex: 1;
    width: 1540px;

    @include mediaMinMax(1025, $normal) {
      max-width: vw-full(1540);
    }
  }

  &__block-controls {
    display: flex;
    flex-direction: column;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }
  }

  &__block-control-btn {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    cursor: pointer;
    color: #595959;
    transition: all 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(60);
      height: vw-full(60);
      border-radius: vw-full(8);
    }

    &:hover {
      background: #f5f5f5;
      border-color: #242424;
    }

    &_delete {
      color: #ff4d4f;

      &:hover {
        background: #fff1f0;
        border-color: #ff4d4f;
      }
    }
  }

  &__add-modules-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 40px 0 64px;

    @include mediaMinMax(1025, $normal) {
      margin: vw-full(40) 0 vw-full(64);
    }
  }

  &__modules-separator {
    width: 1400px;
    height: 2px;
    background: #ddd;
    margin-bottom: 32px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1400);
      height: vw-full(2);
      margin-bottom: vw-full(32);
    }
  }

  &__add-modules-title {
    font-family: Montserrat, sans-serif;
    font-size: 28px;
    font-weight: 500;
    color: #293241;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 32px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
      margin-bottom: vw-full(32);
    }
  }

  &__preview-navigation {
    width: 100%;
    height: 112px;
    display: flex;
    justify-content: center;
    align-items: center;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(112);
    }
    &_mobile {
      width: 412px;
      height: 56px;
    }
  }

  &__preview-nav-container {
    width: 1540px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1540);
    }
    &_mobile {
      width: 100%;
      padding: 16px 28px;
      position: relative;
      justify-content: center;
    }
  }

  &__burger-lines-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 5px;
    position: absolute;
    left: 16px;
  }

  &__burger-line {
    width: 20px;
    height: 1px;
    background-color: #fff;
  }

  &__preview-nav-logo {
    max-width: 300px;
    max-height: 100px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    @include mediaMinMax(1025, $normal) {
      max-width: vw-full(300);
      max-height: vw-full(100);
    }

    img {
      max-width: 300px;
      max-height: 100px;
      object-fit: contain;

      @include mediaMinMax(1025, $normal) {
        max-width: vw-full(300);
        max-height: vw-full(100);
      }
    }
    &_mobile {
      max-height: 24px;
      img {
        max-height: 24px;
      }
    }
  }

  &__preview-nav-links {
    display: flex;
    align-items: center;
    gap: 64px;
    margin-left: 70px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(64);
      margin-left: vw-full(70);
    }
  }

  &__preview-nav-link {
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }
  }

  &__preview-nav-help {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }
  }

  &__preview-nav-help-text {
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }
  }

  &__back-to-top {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 64px 0;
    text-transform: uppercase;
    color: #7F7F7F;
    text-align: center;
    font-family: Montserrat, sans-serif;
    font-size: 22px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: auto;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(64) 0;
      font-size: vw-full(22);
    }

    &_mobile {
      width: 412px;
      font-size: 16px;
      padding: 40px 0;
    }

    &_no-top-padding {
      padding-top: 0;
    }
  }

  &__preview-footer {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 64px 0;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(64) 0;
    }
    &_mobile {
      width: 412px;
      padding: 40px 0;
    }
  }

  &__preview-footer-container {
    width: 1540px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1540);
    }
    &_mobile {
      width: 100%;
      flex-direction: column;
      justify-content: flex-start;
      gap: 24px;
    }
  }

  &__preview-footer-left,
  &__preview-footer-right {
    display: flex;
    align-items: center;
    gap: 56px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(56);
    }
    &_mobile {
      width: 100%;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 18px;
    }
  }

  &__preview-footer-right {
    &_mobile {
      flex-direction: row;
      justify-content: center;
    }
  }

  &__preview-footer-logo {
    max-width: 300px;
    max-height: 100px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    @include mediaMinMax(1025, $normal) {
      max-width: vw-full(300);
      max-height: vw-full(100);
    }

    img {
      max-width: 300px;
      max-height: 100px;
      object-fit: contain;

      @include mediaMinMax(1025, $normal) {
        max-width: vw-full(300);
        max-height: vw-full(100);
      }
    }
  }

  &__preview-footer-fs-logo {
    width: 124px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(124);
      height: vw-full(60);
    }

    svg {
      width: 100%;
      height: 100%;
    }
  }

  &__preview-footer-text {
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: capitalize;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__preview-footer-bold {
    font-weight: 600;
  }

  &__preview-footer-divider {
    width: 1px;
    height: 20px;
    background: #fff;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(20);
    }
  }

  &__preview-footer-pages {
    width: 1540px;
    display: flex;
    align-items: center;
    gap: 120px;
    margin-top: 24px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1540);
      gap: vw-full(120);
      margin-top: vw-full(24);
    }
    &_mobile {
      width: 100%;
      gap: 20px;
      margin-top: 24px;
      flex-direction: column;
    }
  }

  &__preview-footer-page {
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: capitalize;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__mobile-dropdown-menu {
    width: 412px;
    min-height: 915px;
    display: flex;
    flex-direction: column;
    position: fixed;
    right: calc(50% - 412px - 256px - 40px);
    top: 652px;
  }

  &__mobile-menu-header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 20px;
    position: relative;
  }

  &__mobile-menu-close {
    width: 16px;
    height: 16px;
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
  }

  &__mobile-menu-close-line {
    display: block;
    width: 16px;
    height: 1px;
    background: #fff;
    position: absolute;
    top: 50%;
    left: 0;

    &:first-child {
      transform: rotate(45deg);
    }

    &:last-child {
      transform: rotate(-45deg);
    }
  }

  &__mobile-menu-logo {
    max-width: 128px;
    max-height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 128px;
      max-height: 24px;
      object-fit: contain;
    }
  }

  &__mobile-menu-items {
    display: flex;
    flex-direction: column;
    padding: 0 28px;
  }

  &__mobile-menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0;
  }

  &__mobile-menu-item-name {
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    font-weight: 500;
    line-height: normal;
  }
}
</style>
